import React, { useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { Accordion, Button, Col, ProgressBar, Row } from 'react-bootstrap'
import { BsCloudDownloadFill } from 'react-icons/bs'

import { useFetch, useQueryString, useDownload } from '../../../Common'
import { useRequireLogin } from '../../../Auth'
import { useNotifications } from '../../../Notifications'

import { View } from '../../components/View'
import { ComponentHeading } from '../../components/ComponentHeading'
import { Loading } from '../../components/Loading'

import { SignedUrlData } from '../../AppTypes'
import { API_URL, CONTENT_SOURCES } from '../../AppConfig'
import { ContentGetResponse, ContentSources } from '../../../../../cz.vegilove.api/src/_api-models'

import mobileDownloadNotif from '../../../assets/images/mobile-download-notification-300.png?format=webp'
import mobileDownloadNotifDetail from '../../../assets/images/mobile-download-notification-detail-300.png?format=webp'

export const Document = () => {

    const intl = useIntl()
    const { addNotification } = useNotifications()

    const { contentId } = useParams()
    const oneTimeToken = useQueryString( 'token' )

    const auth = useRequireLogin( !oneTimeToken )

    const [ document, setDocument ] = useState<ContentGetResponse & { imgUrl: string | undefined, imgAlt: string | undefined, sourceFile: string, sourceFilename: string, sourceType: ContentSources | undefined } | null>( null )    
    const { state: documentDataRequestState } = useFetch<ContentGetResponse[]>({
        url: `${ API_URL }/content/${ contentId || '' }${ !auth.isAuthenticated && oneTimeToken ? '?token=' + oneTimeToken : '' }`, 
        // if not authenticated or not having token - do not start fetch of document
        noAutoRun: !auth.isAuthenticated && !oneTimeToken,
        label: 'fetch',
        onSuccess: ( state ) => {
            if( state.data?.length === 1 ) {
                const doc = state.data[ 0 ]
                const thumb = doc.attachments?.find( attn => attn.type === 'thumb' )
                const file = doc.attachments?.find( attn => attn.type === 'content' )
                setDocument({
                    ...doc,
                    imgUrl: thumb ? CONTENT_SOURCES[ thumb.source ] + thumb?.filename : undefined,
                    imgAlt: thumb && doc.name,
                    sourceFile: file ? CONTENT_SOURCES[ file.source ] + file?.filename : '',
                    sourceFilename: file ? file?.filename.replace( /^\//i, '' ) : '',
                    sourceType: file?.source
                })
            }
        },
        onError: ( state ) => {
            addNotification({ 
                variant: 'danger', 
                category: 'document-download',
                content: <div>
                    <div><FormattedMessage description="App.Document.Download.ErrorTitle" defaultMessage="Error while preparing document for download" /></div>
                    { state.status === 401 && <div><FormattedMessage description="App.Document.Download.ErrorUnauthorized" defaultMessage="Your download request is not valid. Please, use the link sent to you in e-mail confirming your subscription or login to your profile using your e-mail and password to see all content available for you." /></div>}
                </div>,
            })
        }
    }, [ contentId ] )

    const [ signedUrl, setSignedUrl ] = useState<string|null>( null )
    const { run: runSign } = useFetch<SignedUrlData>({
        url: `${API_URL}/aws/sign/${ contentId || '' }${ !auth.isAuthenticated && oneTimeToken ? '?token=' + oneTimeToken : '' }`,
        // starts only when download is clicked
        noAutoRun: true,
        label: 'sign',
    }, [] )

    const { run: downloadRequestRun, progress: downloadProgress, status: downloadStatus } = useDownload()
    const downloadHandler = useCallback( () => {
        // if the source is aws private and url was not yer signed - lets first sign url and then download
        if( document?.sourceType === 'aws-private' && !signedUrl ) runSign({
            onSuccess: ( state ) => {
                if( state.data?.signedUrl ) {
                    setSignedUrl( state.data.signedUrl || null )
                    downloadRequestRun({ url: state.data.signedUrl, filename: document.sourceFilename })
                }
            }
        })
        else if( document?.sourceFile ) downloadRequestRun({ url: document.sourceFile, filename: document.sourceFilename })
    }, [ document?.sourceFile, document?.sourceFilename, document?.sourceType, downloadRequestRun, runSign, signedUrl ])

    return <View>
        <ComponentHeading>
            <FormattedMessage description='App.Document.Title' defaultMessage='Document download: {name}' values={{ name: document?.name || '...' }} />
        </ComponentHeading>
        <Loading show={ documentDataRequestState.processing } />
        { document && <Row>
            <Col size={ 12 } md={ 6 }>
                { document.imgUrl && <div><img className="w-100" src={ document.imgUrl } alt={ document.imgAlt || '' } /></div> }
            </Col>
            <Col size={ 12 } md={ 6 }>
                <div className="d-grid gap-2">
                    <Button variant="success" 
                        disabled={ documentDataRequestState.processing || downloadStatus === 'run' }
                        onClick={ downloadHandler }
                    >
                        <BsCloudDownloadFill />&nbsp;<FormattedMessage description="App.Document.Download" defaultMessage="Download" />
                    </Button>
                </div>
                { downloadProgress && <ProgressBar now={ downloadStatus === 'finish' ? 100 : downloadProgress.percent * 100 } label={`${ Math.floor( downloadStatus === 'finish' ? 100 : downloadProgress.percent * 100 ) }%`} striped animated />}
                { document.description && <div className='mt-4 p-1 p-md-0' dangerouslySetInnerHTML={{ __html: document.description || '' }}></div> }
            </Col>
            { downloadStatus === 'success' && <Col>
                <div className='py-4'><FormattedMessage description="App.Document.Download.Finish" defaultMessage="Your download has finished. Lookup your document in downloader folder. If you have downloaded the document on your mobile phone, you might need to look in the notification area of your phone to view finished downloads." /></div>
                <Accordion>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header><FormattedMessage description="App.Document.Download.FinishMobileQAButton" defaultMessage="Where I can find my documnet on mobile?" /></Accordion.Header>
                        <Accordion.Body className='text-center'>
                            <img className='p-3 align-top' src={ mobileDownloadNotif.src } alt={ intl.formatMessage({ description: 'App.Document.Download.FinishMobileNotif', defaultMessage: 'Mobile notification of finihed download' }) } />
                            <img className='p-3 align-top' src={ mobileDownloadNotifDetail.src } alt={ intl.formatMessage({ description: 'App.Document.Download.FinishMobileNotifDetail', defaultMessage: 'Detail of finihed download on mobile' }) } />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Col> }
        </Row> }
    </View>
}

export default Document
